import React, { useCallback, useContext, useMemo } from 'react'
import { AttachmentPromiseClient } from '@trustero/trustero-api-web/lib/attachment/attachment_grpc_web_pb'
import { StringValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import { getErrorMessage } from 'src/Utils/globalHelpers'
import { useNewModel } from 'src/components/async/hooks/useNewModel'
import {
  DeleteDocumentRequest,
  DeleteEvidenceGroupRequest,
  EvidenceGroupId,
  UnlinkEvidenceFromControlRequest,
  UnlinkEvidenceFromRequestRequest,
} from '@trustero/trustero-api-web/lib/attachment/attachment_pb'

import styled from 'styled-components/macro'
import isFunction from 'lodash/isFunction'
import {
  useBulkDeleteEvidenceGroupModal,
  useBulkDeleteEvidenceModal,
  useDeleteEvidenceInstanceOrEvidence,
} from 'src/pages/Controls/ControlsShowPage/EvidenceGrid/evidence.hooks'
import { RpcError } from 'grpc-web'
import log from 'loglevel'
import { EvidenceLinkedControlsCount } from 'src/pages/Evidence/evidence.components'
import { Message } from 'src/components/Reusable/Message/Message'
import { MessageType } from 'src/components/Reusable/Message/Message.constants'
import { useConfirmationModal } from '../useConfirmationModal'
import { ConfirmationContext } from '../../../Confirmation'
import { useAuthorizedGrpcClient } from '../../../adapter'
import palette from '../../../designSystem/variables/palette'
import { useHardEvidenceInvalidation } from '../../../Utils/swrCacheInvalidation/useInvalidateEvidence'
import { DeleteEvidenceBody } from './evidence.styles'
const FromRequestBodyStyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2em;
  p {
    font-size: 14px;
    color: ${palette.neutral[900]};
  }
`

const UnlinkControlBody = ({ name }: { name?: string }): JSX.Element => {
  const controlName = name || 'the control'
  return (
    <div>
      <DeleteEvidenceBody>
        <p>{`This evidence will be removed from ${controlName}.`}</p>
      </DeleteEvidenceBody>
    </div>
  )
}

const UnlinkRequestBody = (): JSX.Element => (
  <div>
    <DeleteEvidenceBody>
      <p>
        This evidence will be removed from the request and controls linked to
        the request.
      </p>
    </DeleteEvidenceBody>
  </div>
)

const FromControlBody = ({
  bulkDeleteSection,
  caption,
  isUnlink,
  evidenceInstanceId,
  evidenceLinkedControlsCount,
}: {
  bulkDeleteSection: JSX.Element
  caption: string
  isUnlink?: boolean
  evidenceInstanceId?: string
  evidenceLinkedControlsCount?: number
}): JSX.Element => {
  return (
    <div>
      <DeleteEvidenceBody>
        {isUnlink && (
          <p>{`"${caption}" will be unlinked from this control.`}</p>
        )}
        {!isUnlink && <p>This cannot be undone.</p>}
        {evidenceInstanceId && (
          <EvidenceLinkedControlsCount
            evidenceInstanceId={evidenceInstanceId}
            controlCount={evidenceLinkedControlsCount}
          />
        )}
        {bulkDeleteSection}
      </DeleteEvidenceBody>
    </div>
  )
}

const UnlinkFromControlBody = ({
  caption,
  isViaRequest = false,
}: {
  caption: string
  isViaRequest?: boolean
}): JSX.Element => {
  return (
    <DeleteEvidenceBody>
      <p>
        {isViaRequest
          ? `"${caption}" will be unlinked from the relevant requests and all controls linked to those requests.`
          : `"${caption}" will be unlinked from this control.`}
      </p>
    </DeleteEvidenceBody>
  )
}

const FromRequestBody = ({
  bulkDeleteSection,
  caption,
  isUnlink,
  evidenceInstanceId,
  evidenceLinkedControlsCount,
}: {
  bulkDeleteSection: JSX.Element
  caption: string
  isUnlink?: boolean
  evidenceInstanceId?: string
  evidenceLinkedControlsCount?: number
}): JSX.Element => (
  <FromRequestBodyStyledContainer>
    {isUnlink ? (
      <p>{`"${caption}" will be unlinked from this request and all controls linked to this request.`}</p>
    ) : (
      <>
        <p>The evidence will disappear for all linked controls.</p>
        <p>
          You should clean up bad data, but be careful not to delete evidence
          that your auditor needs to see.
          <br />
          This cannot be undone.
        </p>
      </>
    )}
    {evidenceInstanceId && (
      <EvidenceLinkedControlsCount
        evidenceInstanceId={evidenceInstanceId}
        controlCount={evidenceLinkedControlsCount}
      />
    )}
    {bulkDeleteSection}
  </FromRequestBodyStyledContainer>
)

const UnlinkFromRequestBody = ({
  caption,
}: {
  caption: string
}): JSX.Element => (
  <FromRequestBodyStyledContainer>
    <p>{`"${caption}" will be unlinked from this request and all controls linked to this request.`}</p>
  </FromRequestBodyStyledContainer>
)

type useDeleteEvidenceTypes = {
  evidenceId: string
  caption: string
  controlId: string
  total?: number
  requestId?: string
  evidenceInstanceId?: string
  newEvidenceId?: string
  evidenceLinkedControlsCount?: number
  onUpdate?: () => Promise<void>
}

export const useDeleteEvidence = ({
  evidenceId,
  requestId,
  caption,
  controlId,
  total,
  evidenceInstanceId,
  newEvidenceId,
  evidenceLinkedControlsCount,
  onUpdate,
}: useDeleteEvidenceTypes): (() => void) => {
  const isNewEvidenceDelete = useNewModel()
  const { setConfirmationState } = useContext(ConfirmationContext)
  const bulkDeleteEvidence = useBulkDeleteEvidenceModal({
    caption,
    controlId,
    totalEvidenceCount: total || 0,
    evidenceId: newEvidenceId,
  })

  const refetchEvidence = useHardEvidenceInvalidation()
  const attachmentClient = useAuthorizedGrpcClient(AttachmentPromiseClient)
  const deleteEvidenceInstance = useDeleteEvidenceInstanceOrEvidence()

  const bulkDeleteSection = useMemo(() => {
    if (!controlId && !isNewEvidenceDelete) {
      return <></>
    }
    return (
      <BulkDeleteSection
        total={total || 0}
        bulkDeleteEvidence={bulkDeleteEvidence}
      />
    )
  }, [controlId, total, bulkDeleteEvidence, isNewEvidenceDelete])

  const deleteDocument = useCallback(async () => {
    try {
      if (isNewEvidenceDelete && evidenceId) {
        await deleteEvidenceInstance({ evidenceId, evidenceInstanceId })
      } else {
        const deleteDocumentRequest = new DeleteDocumentRequest()
          .setDocumentId(evidenceId)
          .setDeleteAssociatedTests(true)
        if (requestId) {
          deleteDocumentRequest.setRequestId(
            new StringValue().setValue(requestId),
          )
        }
        await attachmentClient.deleteDocument(deleteDocumentRequest)
      }
      await onUpdate?.()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setConfirmationState({
        isShown: true,
        size: 'lg',
        title: 'Error Deleting Evidence',
        body: <p>{err.message}</p>,
        confirmText: 'OK',
        hideCancel: true,
        onConfirmCB: () => null,
      })
    } finally {
      refetchEvidence()
    }
  }, [
    attachmentClient,
    evidenceId,
    evidenceInstanceId,
    isNewEvidenceDelete,
    requestId,
    refetchEvidence,
    setConfirmationState,
    deleteEvidenceInstance,
    onUpdate,
  ])

  const confirmationModalProps = {
    title: 'Are you sure you want to delete the evidence?',
    body: requestId ? (
      <FromRequestBody
        bulkDeleteSection={bulkDeleteSection}
        caption={caption}
        evidenceInstanceId={evidenceInstanceId}
        evidenceLinkedControlsCount={evidenceLinkedControlsCount}
      />
    ) : (
      <FromControlBody
        bulkDeleteSection={bulkDeleteSection}
        caption={caption}
        evidenceInstanceId={evidenceInstanceId}
        evidenceLinkedControlsCount={evidenceLinkedControlsCount}
      />
    ),
    confirmText: 'Delete Evidence',
    onConfirmCB: deleteDocument,
  }

  return useConfirmationModal(confirmationModalProps)
}

export const useUnlinkEvidenceFromControl = ({
  evidenceId,
  controlId,
  caption,
  isEvidence = false,
  controlName,
  mutateFunc,
  isViaRequest = false,
}: {
  evidenceId: string
  controlId: string
  caption: string
  isEvidence?: boolean
  controlName?: string
  mutateFunc?: () => Promise<void>
  isViaRequest?: boolean
}): (() => void) => {
  const { setConfirmationState } = useContext(ConfirmationContext)
  const attachmentClient = useAuthorizedGrpcClient(AttachmentPromiseClient)

  const unlinkEvidenceFromControl = async () => {
    try {
      const unlinkEvidenceReq = new UnlinkEvidenceFromControlRequest()
        .setEvidenceId(evidenceId)
        .setControlId(controlId)
      await attachmentClient.unlinkEvidenceFromControl(unlinkEvidenceReq)
    } catch (err: unknown) {
      const resError = err as RpcError
      const errorMessage = getErrorMessage(resError)
      if (errorMessage.shouldLog) {
        log.error('Error in Unlink Evidence modal:', errorMessage.message)
      }
      setConfirmationState({
        isShown: true,
        size: 'lg',
        title: 'Error Unlinking Evidence',
        body: <p>{errorMessage.message}</p>,
        confirmText: 'OK',
        hideCancel: true,
        onConfirmCB: () => null,
      })
    } finally {
      isFunction(mutateFunc) && (await mutateFunc())
    }
  }
  const confirmationModalProps = {
    title: isEvidence
      ? 'Are you sure you want to unlink the evidence?'
      : 'Unlink control and remove evidence?',
    body: isEvidence ? (
      <UnlinkFromControlBody caption={caption} isViaRequest={isViaRequest} />
    ) : (
      <UnlinkControlBody name={controlName} />
    ),
    confirmText: isEvidence ? 'Unlink Evidence' : 'Unlink Control',
    onConfirmCB: unlinkEvidenceFromControl,
  }

  return useConfirmationModal(confirmationModalProps)
}

export const useUnlinkEvidenceFromRequest = ({
  evidenceId,
  requestId,
  caption,
  isEvidence = false,
  mutateFunc,
}: {
  evidenceId: string
  requestId: string
  caption: string
  isEvidence?: boolean
  mutateFunc?: () => Promise<unknown>
}): (() => void) => {
  const { setConfirmationState } = useContext(ConfirmationContext)
  const attachmentClient = useAuthorizedGrpcClient(AttachmentPromiseClient)

  const unlinkEvidenceFromRequest = async () => {
    try {
      const unlinkEvidenceReq = new UnlinkEvidenceFromRequestRequest()
        .setEvidenceId(evidenceId)
        .setRequestId(requestId)
      await attachmentClient.unlinkEvidenceFromRequest(unlinkEvidenceReq)
      await mutateFunc?.()
    } catch (err: unknown) {
      const resError = err as RpcError
      const errorMessage = getErrorMessage(resError)
      if (errorMessage.shouldLog) {
        log.error('Error in Unlink Evidence modal:', errorMessage.message)
      }
      setConfirmationState({
        isShown: true,
        size: 'lg',
        title: 'Error Unlinking Evidence',
        body: <p>{errorMessage.message}</p>,
        confirmText: 'OK',
        hideCancel: true,
        onConfirmCB: () => null,
      })
    } finally {
      isFunction(mutateFunc) && (await mutateFunc())
    }
  }
  const confirmationModalProps = {
    title: isEvidence
      ? 'Are you sure you want to unlink the evidence?'
      : 'Unlink request and remove evidence?',
    body: isEvidence ? (
      <UnlinkFromRequestBody caption={caption} />
    ) : (
      <UnlinkRequestBody />
    ),
    confirmText: isEvidence ? 'Unlink Evidence' : 'Unlink Request',
    onConfirmCB: unlinkEvidenceFromRequest,
  }

  return useConfirmationModal(confirmationModalProps)
}

export const useDeleteEvidenceGroup = ({
  caption,
  contentId,
  discoveryId,
  total,
  evidenceId,
  evidenceInstanceId,
  evidenceLinkedControlsCount,
  onUpdate,
}: {
  caption: string
  contentId: string
  discoveryId: string
  total?: number
  evidenceId?: string
  evidenceInstanceId?: string
  evidenceLinkedControlsCount?: number
  onUpdate: () => Promise<void>
}): (() => void) => {
  const isNewEvidenceDelete = useNewModel()
  const { setConfirmationState } = useContext(ConfirmationContext)
  const bulkDeleteEvidenceGroup = useBulkDeleteEvidenceGroupModal({
    caption,
    totalEvidenceCount: total || 0,
    evidenceId,
  })

  const attachmentClient = useAuthorizedGrpcClient(AttachmentPromiseClient)
  const deleteEvidenceInstance = useDeleteEvidenceInstanceOrEvidence()

  const bulkDeleteSection = useMemo(
    () => (
      <BulkDeleteSection
        total={total || 0}
        bulkDeleteEvidence={bulkDeleteEvidenceGroup}
      />
    ),
    [total, bulkDeleteEvidenceGroup],
  )

  const deleteEvidenceGroup = useCallback(async () => {
    try {
      if (isNewEvidenceDelete && evidenceId) {
        await deleteEvidenceInstance({ evidenceId, evidenceInstanceId })
      } else {
        if (!contentId) {
          throw new Error(
            'Content ID is required to delete a piece of evidence',
          )
        }
        const groupId = new EvidenceGroupId()
          .setCaption(new StringValue().setValue(caption))
          .setContentId(new StringValue().setValue(contentId))
        discoveryId &&
          groupId.setDiscoveryId(new StringValue().setValue(discoveryId))
        const deleteEvidenceGroupRequest =
          new DeleteEvidenceGroupRequest().setId(groupId)
        await attachmentClient.deleteEvidenceGroup(deleteEvidenceGroupRequest)
      }
      await onUpdate()
    } catch (err) {
      const resError = err as RpcError
      const errorMessage = getErrorMessage(resError)
      if (errorMessage.shouldLog) {
        log.error('Error in AddCustomFrameworkModal:', errorMessage.message)
      }
      setConfirmationState({
        isShown: true,
        size: 'lg',
        title: 'Error Deleting Evidence',
        body: <p>{errorMessage.message}</p>,
        confirmText: 'OK',
        hideCancel: true,
        onConfirmCB: () => null,
      })
    }
  }, [
    attachmentClient,
    caption,
    contentId,
    discoveryId,
    setConfirmationState,
    isNewEvidenceDelete,
    evidenceId,
    evidenceInstanceId,
    deleteEvidenceInstance,
    onUpdate,
  ])

  const confirmationModalProps = {
    title: 'Are you sure you want to delete the evidence?',
    body: (
      <>
        <p>{`"${caption}" will be deleted. This cannot be undone.`}</p>
        {evidenceInstanceId && (
          <EvidenceLinkedControlsCount
            evidenceInstanceId={evidenceInstanceId}
            controlCount={evidenceLinkedControlsCount}
          />
        )}
        {bulkDeleteSection}
      </>
    ),
    confirmText: 'Delete Evidence',
    onConfirmCB: deleteEvidenceGroup,
  }

  return useConfirmationModal(confirmationModalProps)
}

const BulkDeleteSection = ({
  total,
  bulkDeleteEvidence,
}: {
  total: number
  bulkDeleteEvidence: () => void
}): JSX.Element => {
  return (
    <>
      {total > 1 ? (
        <Message
          messageType={MessageType.INFO}
          messageText={`You are deleting one piece of evidence from a stack that samples the same data over time. Would you rather delete all ${total} pieces of evidence in this stack?`}
          ctaButton={{
            text: 'Delete All Evidence',
            onClick: bulkDeleteEvidence,
          }}
        />
      ) : (
        <></>
      )}
    </>
  )
}
